import { normalizeHistoricSearch } from './historicSearchFilter'
import { createGuardedBeforeSend, type CurrentToken } from './historicToken'

const INVALID =
  'Ricerca non valida: massimo 512 caratteri, 8 parole o frasi, 128 caratteri per parola/frase. Usa virgolette bilanciate, senza operatori speciali.'
const forbidden = '*(){}[]:;,\\|&~=<>^'

const validateCharacters = (text: string): void => {
  let i = 0
  while (i < text.length) {
    const code = text.charCodeAt(i)
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = text.charCodeAt(i + 1)
      if (!(next >= 0xdc00 && next <= 0xdfff)) throw new Error(INVALID)
      i += 2
    } else {
      if (code >= 0xdc00 && code <= 0xdfff) throw new Error(INVALID)
      i++
    }
  }
  if (/[\p{Cc}\p{Cf}]/u.test(text)) throw new Error(INVALID)
  for (const char of text) if (char.charCodeAt(0) < 128 && forbidden.includes(char)) throw new Error(INVALID)
}

/** Client validation only. The server remains authoritative; never echo invalid input. */
export const normalizeNativeHistoricSearch = (value: unknown): string | null => {
  const text = normalizeHistoricSearch(value)
  if (text === null) return null
  if (text.length > 512) throw new Error(INVALID)
  validateCharacters(typeof value === 'string' ? value : text)
  let position = 0
  let count = 0
  while (position < text.length) {
    while (position < text.length && /\s/u.test(text[position])) position++
    if (position === text.length) break
    const phrase = text[position] === '"'
    const start = phrase ? ++position : position
    if (phrase) {
      while (position < text.length && text[position] !== '"') position++
      if (position === text.length || position === start) throw new Error(INVALID)
    } else {
      while (position < text.length && !/\s/u.test(text[position])) {
        if (text[position] === '"') throw new Error(INVALID)
        position++
      }
    }
    const unit = text.slice(start, position)
    if (unit.length > 128 || ++count > 8) throw new Error(INVALID)
    const normalized = unit
      .split(/\s+/u)
      .filter((part) => part.length > 0)
      .join(' ')
      .normalize('NFC')
    validateCharacters(normalized)
    if (normalized.length > 128 || !/[\p{L}\p{Nd}]/u.test(normalized)) throw new Error(INVALID)
    if (phrase) {
      position++
      if (position < text.length && !/\s/u.test(text[position])) throw new Error(INVALID)
    }
  }
  return text
}

/** Quote the OData literal FIRST; percent-encode its alias value exactly ONCE. */
export const historicTicketUrl = (host: string, search: string | null): string => {
  const base = host.replace(/\/$/u, '')
  if (search === null) return `${base}/ticket`
  const text = normalizeNativeHistoricSearch(search)
  if (text === null) throw new Error(INVALID)
  const literal = `'${text.replace(/'/gu, "''")}'`
  const encoded = encodeURIComponent(literal).replace(
    /[!'()*]/gu,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`,
  )
  return `${base}/TicketSearchV1(text=@text)?@text=${encoded}`
}

type Request = { url: string; method: string; params: Record<string, unknown> }

/** Explicit opt-in for the native search total ('Totale e gruppi'). Absent = fast path. */
export type HistoricCountOptIn = {
  /** Runs synchronously before every counted request; throws to refuse a stale request. */
  beforeRequest: () => void
}

const NATIVE_PARAMS = ['$filter', '$orderby', '$skip', '$top', '$select', '$expand']

/** Keep current-token/header configuration, then pin ONLY this committed native
 * root load against a legacy URL rewrite. Never strip legitimate field filters.
 * Without `countOptIn` any $count is refused; with it, only the literal `$count=true` that
 * DevExtreme sends on the same paged root request (or its `$top=0` count-only form). */
export const createHistoricBeforeSend = <T extends Request>(
  token: CurrentToken,
  configure: (bearer: string, timeoutMs: number) => (request: T) => T,
  url: string,
  native: boolean,
  countOptIn?: HistoricCountOptIn,
  /** Refuses (throws) requests of a store the page already retired; checked first, synchronously. */
  admit?: () => void,
) => {
  const guarded = createGuardedBeforeSend(token, configure, 180_000)
  return (request: T): T => {
    admit?.()
    const supported = (params: Record<string, unknown>) =>
      Object.entries(params).every(
        ([key, value]) =>
          NATIVE_PARAMS.includes(key) || (countOptIn !== undefined && key === '$count' && value === 'true'),
      )
    if (native && (request.method.toUpperCase() !== 'GET' || request.url !== url || !supported(request.params))) {
      throw new Error('Richiesta di ricerca non supportata. Nessuna ricerca alternativa è stata inviata.')
    }
    if (native && countOptIn) countOptIn.beforeRequest()
    const result = guarded(request)
    if (native) {
      if (!supported(result.params)) throw new Error('Richiesta di ricerca non supportata.')
      result.url = url
    }
    return result
  }
}

/** DevExtreme OData errors carry httpStatus; do not display native exception prose. */
export const nativeHistoricErrorMessage = (error: unknown): string => {
  const status = typeof error === 'object' && error !== null && 'httpStatus' in error ? error.httpStatus : undefined
  if (status === 404)
    return 'Ricerca non disponibile in questa versione API. Contatta il supporto o cancella il testo per tornare alla consultazione.'
  if (status === 503)
    return 'Ricerca temporaneamente non disponibile. Riprova più tardi o cancella il testo per tornare alla consultazione.'
  if (status === 400)
    return 'Ricerca o filtri non validi. Controlla il testo e i filtri; se necessario ricarica la pagina.'
  return 'Ricerca non riuscita. Controlla la sessione o cancella il testo per tornare alla consultazione.'
}
